import {useCallback, useEffect, useMemo, useState} from 'react';
import {useLazyQuery, useQuery} from '@apollo/client';
import {useNavigate} from 'react-router-dom';
import {Button, Dropdown} from 'antd';
import {
  DownOutlined,
  PlusOutlined,
  ReloadOutlined,
  SettingOutlined,
  WalletOutlined,
} from '@ant-design/icons';
import {useSelector} from 'react-redux';
import {toast} from 'react-toastify';

import {
  GENERATE_CURRENCY_EXPOSURE,
  GENERATE_LIQUIDITY_REPORT,
  GET_ALL_WALLET_PAYMENT_PROVIDERS,
} from 'graphql/queries/wallet';
import DBModal from 'components/modal/modal';
import {makeSelectTabOperations} from 'redux/store/auth';
import {NavTabsEnum} from 'utils/constants';
import {useVisibleSubTabs} from 'utils/useVisibleSubTabs';
import {getMenuItem} from 'utils/helper';
import {ConfirmModal} from 'pages/adminOpportunities/stocksListings/ViewOrders/HandleOrders';

import UpdateGlobalWalletSettingsModal from './modal/UpdateGlobalWalletSettingsModal';
import TotalCashBalance from './view/totalCashBalance/TotalCashBalance';
import SwapSettings from './Swap/settings/SwapSettings';
import ReportFilters from './view/reports/Reports';
import AllMobileMoneyOperators from './mobileMoney/loadTable/AllMobileMoney';
import CurrencyFloat from './overview/CurrencyFloat';
import CurrenciesTable from './overview/CurrenciesTable';
import ProvidersTable from './overview/ProvidersTable';
import styles from './wallet.module.scss';

const WALLET_SUBTABS = [
  {key: 'supported', value: 'SUPPORTED', label: 'Currencies'},
  {key: 'providers', value: 'PLATFORM', label: 'Payment providers'},
  {key: 'mobile-money', value: 'MOBILE-MONEY', label: 'Mobile money'},
];

const REPORTS = [
  {key: 'LIQUIDITY', label: 'Liquidity report', confirm: true},
  {key: 'TRANSACTION', label: 'Transaction report', confirm: false},
  {key: 'CURRENCY_EXPOSURE', label: 'Currency exposure report', confirm: true},
  {key: 'RECONCILIATION', label: 'Reconciliation report', confirm: false},
  {key: 'CUSTOMER_ACTIVITY', label: 'Customer activity report', confirm: false},
];

const Wallet = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('SUPPORTED');
  const {visibleSubTabs, visibleKeys, firstVisible} = useVisibleSubTabs(
    NavTabsEnum.Wallet,
    WALLET_SUBTABS,
  );

  useEffect(() => {
    const current = WALLET_SUBTABS.find(sub => sub.value === activeTab);
    if (firstVisible && (!current || !visibleKeys.includes(current.key))) {
      setActiveTab(firstVisible.value);
    }
  }, [firstVisible, visibleKeys, activeTab]);

  const [openCashBalanceModal, setOpenCashBalanceModal] = useState(false);
  const [swapSettingsModalDetails, setSwapSettingsModalDetails] = useState({});
  const [openUpdateModal, setOpenUpdateModal] = useState(false);
  const [openFilterModal, setOpenFilterModal] = useState(false);
  const [reportType, setReportType] = useState('');
  const [openConfirmModal, setOpenConfirmModal] = useState(false);

  const {data, loading, refetch} = useQuery(GET_ALL_WALLET_PAYMENT_PROVIDERS, {
    fetchPolicy: 'no-cache',
    errorPolicy: 'ignore',
  });

  const [generateLiquidityReport, {loading: liquidity}] = useLazyQuery(
    GENERATE_LIQUIDITY_REPORT,
  );
  const [generateCurrencyExposureReport, {loading: currencyExposure}] =
    useLazyQuery(GENERATE_CURRENCY_EXPOSURE);

  const tabOperations = useSelector(state =>
    makeSelectTabOperations(state, NavTabsEnum.Wallet),
  );
  const swapOperations = useSelector(state =>
    makeSelectTabOperations(state, NavTabsEnum.Swap),
  );

  const currencies = data?.getPlatformSupportedCurrenciesForAdmin?.currencies;
  const providers = data?.getAllPaymentProviders?.providers;
  const operators = data?.getMobileMoneyOperatorsForAdmin?.operators;

  const counts = useMemo(
    () => ({
      SUPPORTED: currencies?.length,
      PLATFORM: providers?.length,
      'MOBILE-MONEY': operators?.length,
    }),
    [currencies, providers, operators],
  );

  const handleGenerate = () => {
    if (reportType === 'LIQUIDITY') {
      generateLiquidityReport()
        .then(({data: response}) =>
          toast.success(response?.generateLiquidityReport?.responseMessage),
        )
        .finally(() => setOpenConfirmModal(false));
    } else if (reportType === 'CURRENCY_EXPOSURE') {
      generateCurrencyExposureReport()
        .then(({data: response}) =>
          toast.success(
            response?.generateCurrencyExposureReport?.responseMessage,
          ),
        )
        .finally(() => setOpenConfirmModal(false));
    }
  };

  /** Row actions for a platform currency. */
  const buildCurrencyActionItems = useCallback(
    record => {
      const items = [];
      if (tabOperations?.update) {
        items.push({
          key: 'update',
          label: 'Update currency',
          onClick: () =>
            navigate(
              `/dashboard/wallet/update-wallet-currency/${record?.currency}`,
            ),
        });
        items.push({
          key: 'swap',
          label: 'Swap settings',
          onClick: () =>
            setSwapSettingsModalDetails({
              open: true,
              currency: record?.currency,
              isEnabled: record?.swap?.sourceCurrencyEnabledForSwap,
              minAmount: record?.swap?.minSwapAmountInSourceCurrency,
              maxAmount: record?.swap?.maxSwapAmountInSourceCurrency,
            }),
        });
      }
      if (swapOperations?.view) {
        items.push({
          key: 'swapTransactions',
          label: 'Swap transactions',
          onClick: () =>
            navigate(`/dashboard/wallet/swap-transactions/${record?.currency}`),
        });
      }
      return items;
    },
    [navigate, swapOperations?.view, tabOperations?.update],
  );

  /** Row actions for one provider/currency pair inside the providers table. */
  const buildProviderCurrencyActionItems = useCallback(
    row => {
      const items = [];
      if (tabOperations?.update) {
        items.push({
          key: 'update',
          label: 'Update currency',
          onClick: () =>
            navigate(
              `/dashboard/wallet/view-details/${row?.provider}/${row?.currency}`,
            ),
        });
      }
      items.push({
        key: 'transactions',
        label: 'View transactions',
        onClick: () =>
          navigate(
            `/dashboard/wallet/transactions/${row?.provider}/${row?.currency}`,
          ),
      });
      return items;
    },
    [navigate, tabOperations?.update],
  );

  const createItems = [
    {
      key: 'currency',
      label: 'Currency settings',
      onClick: () => navigate('/dashboard/wallet/create-wallet-currency'),
    },
    {
      key: 'provider',
      label: 'Provider settings',
      onClick: () => navigate('/dashboard/wallet/create-provider-settings'),
    },
    {
      key: 'mobile-money',
      label: 'Mobile money settings',
      onClick: () => navigate('/dashboard/wallet/mobile-money'),
    },
  ];

  const reportItems = REPORTS.map(report => ({
    key: report.key,
    label: report.label,
    onClick: () => {
      setReportType(report.key);
      if (report.confirm) setOpenConfirmModal(true);
      else setOpenFilterModal(true);
    },
  }));

  const transactionItems = useMemo(
    () => ({
      onClick: event =>
        navigate(`/dashboard/wallet/transactions/all/${event.key}`),
      mode: 'inline',
      items: currencyMenuItems,
    }),
    [navigate],
  );

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.titleWrap}>
          <h1 className={styles.title}>Wallet</h1>
          <p className={styles.subtitle}>
            Currencies the platform supports, the providers that move the money,
            and the mobile money operators behind them.
          </p>
        </div>
        <div className={styles.headerActions}>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => refetch()}
            loading={loading}>
            Refresh
          </Button>
          <Dropdown
            trigger={['click']}
            disabled={currencyExposure || liquidity || !tabOperations?.create}
            menu={{items: reportItems}}>
            <Button disabled={currencyExposure || liquidity}>
              Reports <DownOutlined />
            </Button>
          </Dropdown>
          <Button
            icon={<SettingOutlined />}
            disabled={!tabOperations?.update}
            onClick={() => setOpenUpdateModal(true)}>
            General settings
          </Button>
          <Dropdown
            trigger={['click']}
            disabled={!tabOperations?.create}
            menu={{items: createItems}}>
            <Button
              type="primary"
              className={styles.primaryButton}
              icon={<PlusOutlined />}
              disabled={!tabOperations?.create}>
              Create <DownOutlined />
            </Button>
          </Dropdown>
        </div>
      </div>

      <CurrencyFloat providers={providers} />

      <div className={styles.tabs}>
        {visibleSubTabs.map(tab => (
          <button
            type="button"
            key={tab.key}
            className={`${styles.tab} ${
              activeTab === tab.value ? styles.tabActive : ''
            }`}
            onClick={() => setActiveTab(tab.value)}>
            {tab.label}
            {counts[tab.value] !== undefined && (
              <span className={styles.tabCount}>{counts[tab.value]}</span>
            )}
          </button>
        ))}
      </div>

      <div className={styles.toolbar}>
        <span className={styles.toolbarHint}>
          {activeTab === 'SUPPORTED' &&
            'Expand a row to see exactly which deposit and withdrawal methods are on.'}
          {activeTab === 'PLATFORM' &&
            'Expand a provider to see its balance in each currency.'}
          {activeTab === 'MOBILE-MONEY' &&
            'Operators available to users in each country.'}
        </span>
        <div className={styles.toolbarActions}>
          <Dropdown trigger={['click']} menu={transactionItems}>
            <Button icon={<WalletOutlined />}>
              Transactions <DownOutlined />
            </Button>
          </Dropdown>
          <Button onClick={() => setOpenCashBalanceModal(true)}>
            Total users balance
          </Button>
          <Button
            disabled={!swapOperations?.view}
            onClick={() => navigate('/dashboard/wallet/swap-transactions/All')}>
            Swap transactions
          </Button>
        </div>
      </div>

      {activeTab === 'SUPPORTED' && (
        <CurrenciesTable
          data={currencies}
          loading={loading}
          buildActionItems={buildCurrencyActionItems}
        />
      )}
      {activeTab === 'PLATFORM' && (
        <ProvidersTable
          data={providers}
          loading={loading}
          buildCurrencyActionItems={buildProviderCurrencyActionItems}
        />
      )}
      {activeTab === 'MOBILE-MONEY' && (
        <AllMobileMoneyOperators
          loading={loading}
          operations={tabOperations}
          data={operators}
        />
      )}

      <DBModal
        isOpen={openUpdateModal}
        handleClose={() => setOpenUpdateModal(false)}
        content={
          <UpdateGlobalWalletSettingsModal
            handleCloseModal={setOpenUpdateModal}
          />
        }
      />
      <DBModal
        isOpen={openCashBalanceModal}
        handleClose={() => setOpenCashBalanceModal(false)}
        content={<TotalCashBalance />}
      />
      <DBModal
        isOpen={swapSettingsModalDetails.open}
        handleClose={() => setSwapSettingsModalDetails({open: false})}
        width={560}
        content={
          <SwapSettings
            refetch={refetch}
            closeModal={() => setSwapSettingsModalDetails({open: false})}
            data={swapSettingsModalDetails}
          />
        }
      />
      <DBModal
        isOpen={openFilterModal}
        handleClose={() => setOpenFilterModal(false)}
        content={
          <ReportFilters
            reportType={reportType}
            setOpenModal={setOpenFilterModal}
          />
        }
      />
      <ConfirmModal
        title="Confirm Report Generation"
        openModal={openConfirmModal}
        setOpenModal={setOpenConfirmModal}
        confirmText="Are you sure you want to GENERATE this report?"
        loading={currencyExposure || liquidity}
        handleOperation={handleGenerate}
      />
    </div>
  );
};

/**
 * The per-currency transactions menu. Each currency expands to All / Deposits /
 * Withdrawals, and the leaf key becomes the route segment.
 */
const currencyMenuItems = [
  ['USD', 'USD'],
  ['EURO', 'EUR'],
  ['XOF', 'XOF'],
  ['XAF', 'XAF'],
  ['NGN', 'NGN'],
  ['CAD', 'CAD'],
  ['GBP', 'GBP'],
].map(([label, code], index) =>
  getMenuItem(label, `sub${index}`, <SettingOutlined />, [
    getMenuItem(
      label,
      `group${index}`,
      null,
      [
        getMenuItem('All', code),
        getMenuItem('Deposits', `DEPOSIT/${code}`),
        getMenuItem('Withdrawals', `WITHDRAWAL/${code}`),
      ],
      'group',
    ),
  ]),
);

export default Wallet;
